import { Screen, useNavigate, useParams, useScreen, useStep } from "flemo";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { AuthGuard } from "@/components/AuthGuard";
import { BottomNav } from "@/components/BottomNav";
import { PaywallDialog } from "@/components/PaywallDialog";
import {
  useFavoriteIdsQuery,
  useIsPlusQuery,
  useLearnedCountsQuery,
  useSearchWordsQuery,
  useWordSetsQuery,
} from "@/hooks/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { trackEvent } from "@/lib/analytics";
import { fadeTransition } from "@/lib/transitions";

import { CategoryCard } from "./_components/CategoryCard";
import { CompletedModal } from "./_components/CompletedModal";
import FavoriteCard, { EMPTY_FAVORITES_TOAST_ID } from "./_components/FavoriteCard";
import { FlashcardsSkeleton } from "./_components/FlashcardsSkeleton";
import { Header } from "./_components/Header";
import { SearchResults } from "./_components/SearchResults";

export function FlashcardsPage() {
  const { isActive } = useScreen();
  const { push, replace } = useNavigate();
  const { openCompletedModal, completedWordSetId } = useParams<"/flashcards">();
  const { popStep, pushStep } = useStep<"/flashcards">();

  const { data: wordSets = [], isPending } = useWordSetsQuery();
  const { data: isPlus = false } = useIsPlusQuery();
  const { data: learnedCounts = {} } = useLearnedCountsQuery();
  const { data: favoriteIds = [] } = useFavoriteIdsQuery();

  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallSource, setPaywallSource] = useState("unknown");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  const {
    data: searchData,
    isPending: isSearchPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useSearchWordsQuery(debouncedQuery);

  const searchResults = searchData?.items ?? [];

  const favoriteCount = favoriteIds.length;
  const isSearching = searchQuery.trim().length > 0;

  // 검색 이벤트 트래킹 (디바운스된 쿼리 기준)
  useEffect(() => {
    if (debouncedQuery.trim().length >= 2) {
      trackEvent("word_searched", {
        query: debouncedQuery.trim(),
        result_count: searchResults.length,
      });
    }
  }, [debouncedQuery, searchResults.length]);

  useEffect(() => {
    return () => {
      toast.dismiss(EMPTY_FAVORITES_TOAST_ID);
    };
  }, [isActive]);

  const handleGoToTests = () => {
    popStep();
    setTimeout(() => replace("/tests", {}, { transitionName: fadeTransition.name }), 100);
  };

  const handleRestartAll = () => {
    const setId = completedWordSetId;

    if (!setId) {
      return;
    }

    popStep();
    setTimeout(() => push("/flashcards/:id", { id: setId, restartFromScratch: true }), 100);
  };

  return (
    <AuthGuard>
      <Screen sharedNavigationBar={<BottomNav />}>
        <main className="flex h-dvh w-full flex-col overflow-hidden bg-tertiary pt-safe">
          {/* 상단 헤더 영역 (검색바 포함) */}
          <Header searchQuery={searchQuery} onSearchQueryChange={setSearchQuery} />

          <section
            className={`mx-auto flex w-full max-w-4xl flex-1 min-h-0 flex-col overflow-y-auto overscroll-none pb-[calc(80px+env(safe-area-inset-bottom,0px))] scrollbar-hidden ${isSearching ? "bg-card" : "gap-5"}`}
          >
            {isSearching ? (
              /* 검색 결과 */
              <SearchResults
                results={searchResults}
                query={debouncedQuery}
                isPlus={isPlus}
                isPending={
                  searchQuery.trim() !== debouncedQuery.trim() || (isSearchPending && debouncedQuery.trim().length >= 1)
                }
                hasNextPage={hasNextPage ?? false}
                isFetchingNextPage={isFetchingNextPage}
                onFetchNextPage={fetchNextPage}
                onPaywallOpen={() => {
                  trackEvent("paywall_triggered", { source: "word_search" });
                  setPaywallSource("word_search");
                  setPaywallOpen(true);
                }}
              />
            ) : isPending ? (
              <FlashcardsSkeleton />
            ) : (
              <>
                {/* 즐겨찾기 카드 */}
                <FavoriteCard
                  isPlus={isPlus}
                  favoriteCount={favoriteCount}
                  onPaywallOpen={() => {
                    trackEvent("paywall_triggered", { source: "favorites" });
                    setPaywallSource("favorites");
                    setPaywallOpen(true);
                  }}
                />

                {/* 세트 목록 */}
                <div className="relative flex flex-col gap-4 px-5">
                  {wordSets.map(wordSet => {
                    const progress = learnedCounts[wordSet.id];
                    return (
                      <CategoryCard
                        key={wordSet.id}
                        wordSet={wordSet}
                        knownCount={progress?.knownCount ?? 0}
                        learningCount={progress?.learningCount ?? 0}
                        isLocked={!wordSet.is_free && !isPlus}
                        onLockedClick={() => {
                          trackEvent("paywall_triggered", { source: "locked_word_set", word_set_id: wordSet.id });
                          setPaywallSource("locked_word_set");
                          setPaywallOpen(true);
                        }}
                        onCompleted={() => pushStep({ openCompletedModal: true, completedWordSetId: wordSet.id })}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </main>

        <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} source={paywallSource} />

        {/* 학습 덱을 모두 본 뒤 빈 상태 안내 */}
        <CompletedModal
          open={openCompletedModal ?? false}
          onClose={popStep}
          onRestartAll={handleRestartAll}
          onGoToTests={handleGoToTests}
        />
      </Screen>
    </AuthGuard>
  );
}
